// src/pages/Consultation/Components/SessionCard.jsx
import React, { useState, useEffect } from "react";
import {
  FiUser,
  FiClock,
  FiShoppingCart,
  FiMapPin,
  FiCalendar,
  FiEye,
} from "react-icons/fi";
import Button from "../../../components/common/Button";

const SessionCard = ({ session, onView }) => {
  const [duration, setDuration] = useState(0);

  // Calculate live duration for active sessions
  useEffect(() => {
    if (session.status === "ACTIVE" || session.status === "PAUSED") {
      const calculateDuration = () => {
        const start = new Date(session.startTime);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000 / 60); // minutes
        setDuration(diff);
      };

      calculateDuration();
      const interval = setInterval(calculateDuration, 60000); // Update every minute

      return () => clearInterval(interval);
    } else {
      setDuration(session.duration || 0);
    }
  }, [session.status, session.startTime, session.duration]);

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "PAUSED":
        return "bg-yellow-100 text-yellow-800";
      case "COMPLETED":
        return "bg-blue-100 text-blue-800";
      case "CONVERTED":
        return "bg-purple-100 text-purple-800";
      case "ABANDONED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const customerName =
    session.userId?.name || session.guestCustomer?.name || "Guest Customer";
  const customerPhone =
    session.guestCustomer?.phoneNumber || session.userId?.email || "";

  const isConverted = session.outcome?.converted;

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-all duration-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-medium text-gray-900">{session.sessionCode}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {session.sessionType === "APPOINTMENT"
                ? "📅 Appointment"
                : "🚶 Walk-in"}
            </p>
          </div>
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
              session.status
            )}`}
          >
            {session.status}
          </span>
        </div>

        {/* Duration Timer */}
        <div className="flex items-center text-sm text-gray-600">
          <FiClock className="mr-1" />
          <span className="font-medium">{formatDuration(duration)}</span>
          {(session.status === "ACTIVE" || session.status === "PAUSED") && (
            <span className="ml-2 flex items-center">
              {session.status === "ACTIVE" && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Customer Info */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <FiUser className="text-primary" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{customerName}</p>
            {customerPhone && (
              <p className="text-sm text-gray-500">{customerPhone}</p>
            )}
          </div>
        </div>
      </div>

      {/* Session Details */}
      <div className="p-4 space-y-3">
        {/* Admin */}
        <div className="flex items-center text-sm">
          <FiUser className="text-gray-400 mr-2 flex-shrink-0" />
          <span className="text-gray-600">Admin:</span>
          <span className="ml-auto font-medium text-gray-900 truncate">
            {session.primaryAdmin?.adminName || "N/A"}
          </span>
        </div>

        {/* Location */}
        <div className="flex items-center text-sm">
          <FiMapPin className="text-gray-400 mr-2 flex-shrink-0" />
          <span className="text-gray-600">Branch:</span>
          <span className="ml-auto font-medium text-gray-900">
            {session.branchCode}
          </span>
        </div>

        {/* Cart Info */}
        {isConverted ? (
          <div className="flex items-center text-sm">
            <FiShoppingCart className="text-gray-400 mr-2 flex-shrink-0" />
            <span className="text-gray-600">Purchased :</span>
            <span className="ml-auto font-medium text-gray-900">
              {session.outcome?.itemsPurchased || 0} items
            </span>
          </div>
        ) : (
          <div className="flex items-center text-sm">
            <FiShoppingCart className="text-gray-400 mr-2 flex-shrink-0" />
            <span className="text-gray-600">Cart:</span>
            <span className="ml-auto font-medium text-gray-900">
              {session.cartDetails?.itemCount || 0} items
            </span>
          </div>
        )}

        {/* Cart Value */}
        {/* {isConverted ? (
          <div className="p-2 bg-primary/5 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Order Value:</span>
              <span className="text-lg font-bold text-primary">
                ₹{(session.outcome?.orderValue || 0).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        ) : (
          <div className="p-2 bg-primary/5 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Cart Value:</span>
              <span className="text-lg font-bold text-primary">
                ₹{(session.cartDetails.cartValue || 0).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        )} */}
      </div>

      {/* Actions */}
      <div className="p-4 bg-gray-50 rounded-b-lg">
        <Button
          onClick={() => onView(session._id)}
          className="w-full"
          size="sm"
        >
          <FiEye className="mr-2" />
          View Session
        </Button>
      </div>
    </div>
  );
};

export default SessionCard;
